import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import type {BaseSyntheticEvent, FC, ReactElement} from 'react'
import {useState} from 'react'
import {useUpdateTenantsCurrentUserMutation} from '@apis/infrastructureTenants'
import {useUserContext} from '@containers/App/UserProvider'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useSnackbar} from '@hooks/useSnackbar'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {mergeTenantUserIntoCurrentUser} from '@shared/utils/tenantUser'

const AgreeToTerms: FC = (): ReactElement => {
  const [t] = useCustomTranslation('login')
  const [tCommon] = useCustomTranslation('common')

  const [value, setValue] = useState<string>('terms')
  const [open, setOpen] = useState<boolean>(true)
  const [agreeToTerms] = useState<boolean>(false)
  const [updateUserAgreement, data] = useUpdateTenantsCurrentUserMutation()
  const {currentUser, setCurrentUser} = useUserContext() as UserContextProps
  const [showNotifications] = useSnackbar()

  const handleClose = (): void => {
    setOpen(false)
  }

  const handleUserAgreement = (): void => {
    updateUserAgreement({
      body: {
        has_agreed_to_terms_and_conditions: true,
        has_agreed_to_privacy_policy: true,
      },
    })
      .unwrap()
      .then((data) => {
        setCurrentUser(mergeTenantUserIntoCurrentUser(currentUser, data.user))
        showNotifications({
          message: t('agreed_to_terms_and_conditions'),
          severity: 'success',
        })
        handleClose()
        return
      })
      .catch((error) => {
        console.error(error)
      })
  }

  const handleChange = (event: BaseSyntheticEvent, newValue: string): void => {
    setValue(newValue)
  }

  return (
    <Dialog
      data-testid="mbai-main-agree-to-terms-component"
      fullScreen
      open={open}
      onClose={handleClose}
    >
      <DialogContent>
        <Box
          sx={{
            width: '600px',
            mx: 'auto',
            mt: 5,
          }}
        >
          <Typography variant="h4" sx={{mb: 2}}>
            {t('must_agree_to_terms_and_conditions')}
          </Typography>
          <Box
            data-testid="mbai-main-settings-component"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 3,
            }}
          >
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              <Tab label={t('terms_and_conditions')} value="terms" id="tab_user_profile" />
              <Tab label={t('privacy_policy')} value="privacy" id="tab_account_management" />
            </Tabs>
          </Box>
          {value === 'terms' ? (
            <iframe
              title={t('terms_and_conditions')}
              src="https://www.montblanc.ai/en/terms-conditions"
              width="600px"
              style={{
                border: 'none',
                height: '70vh',
                borderRadius: '5px',
              }}
            />
          ) : value === 'privacy' ? (
            <iframe
              title={t('privacy_policy')}
              src="https://www.montblanc.ai/en/privacy-policy"
              width="600px"
              style={{
                border: 'none',
                height: '70vh',
                borderRadius: '5px',
              }}
            />
          ) : null}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <FormControlLabel
              control={<Checkbox name="agree_to_terms" />}
              label={t('i_agree_to_terms_and_conditions')}
              checked={agreeToTerms}
              onChange={handleUserAgreement}
              sx={{
                m: 0,
                ml: '-11px',
              }}
            />
            {data.isLoading ? <Typography variant="body1">{tCommon('updating')}</Typography> : null}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AgreeToTerms
